
// ============================================================================
// V2 PROGRAM TYPES AND INTERFACES
// ============================================================================

export type DeliveryModeV2 = "hybrid" | "via-zoom" | "on-campus" | "online";
export type DeliveryModeDisplayV2 = "Hybrid" | "Via-Zoom" | "On-Campus" | "Online";

export interface ProgramTypeDataV2 {
  subtitle: string;
  about: string;
  videoUrl?: string;
  imageUrls?: string[];
  nationalCertifications?: any;
  podcastData?: PodcastDataV2[];
  thisCourseIncludesData?: Array<{ id: number; name: string }>;  // Moved from Program to ProgramType
  programComparison?: any;  // Moved from Program to ProgramType
  paymentOptions: PaymentOptionV2[];
  schedule: ProgramScheduleV2;
  keyFeatures: KeyFeatureV2[];
  faqs?: FAQV2[];
  workPlaces?: string[];
}

export interface PaymentOptionV2 {
  id: number;
  sortOrder?: number;
  planType: string;
  displayName: string;
  paymentOptionName: string; // Required field
  numberOfPayments: number;
  amount: number;
  frequency: string;
  squarePlanId: string; // Required field
  youPay: number;
  youSave: string;
  paymentTags: string;
  cardTag: { // Required field
    available: boolean;
    value: string;
  };
  slug: string; // Required field
  currency: string; // Default: 'USD'
}

export interface KeyFeatureV2 {
  id: number;
  name: string;
  icon: string;
  value: string;
  description: string;
}

export interface FAQV2 {
  id: number;
  type: string;
  question: string;
  answer: string;
}

/** One row: month + day-of-month dates. Backend expands to YYYY-MM-DD per year. */
export interface ScheduleBatchEntry {
  month: string; // Full name: January .. December
  dates: string[]; // Day of month as strings e.g. ["5", "15", "25"]
}

// Backend V2 Simple Schedule Schema (matches actual backend Zod)
export interface ProgramScheduleV2 {
  // For hybrid/online types (flat structure)
  month?: string;
  year?: number;
  /** Calendar dates mode (YYYY-MM-DD). */
  dates?: string[];
  /** Days of month (1-31) repeated every month, every year. When set, backend expands to YYYY-MM-DD for range. */
  recurringDaysOfMonth?: number[];
  /** New shape: month + dates per row. Replaces/supplements recurringDaysOfMonth. */
  scheduleBatches?: ScheduleBatchEntry[];
  /** Rolling/daily enrollment: no fixed start dates — students always start the next calendar day. Mutually exclusive with dates/recurringDaysOfMonth/scheduleBatches. */
  isDailyEnrollment?: boolean;

  // For via-zoom/on-campus types (flat batches array)
  batches?: Array<{
    batch?: string;
    dates?: string[];
    /** Days of month (1-31) for this batch, every month every year. */
    recurringDaysOfMonth?: number[];
    /** Month + dates per row for this batch (same as scheduleBatches at schedule level). */
    scheduleBatches?: ScheduleBatchEntry[];
    daysOfWeek?: string[];
    time?: string;
  }>;
}

// ============================================================================
// MAIN PROGRAM V2 INTERFACE
// ============================================================================

export interface ProgramV2 {
  // Basic Information
  programId?: string; // Auto-generated, optional in request
  title: string;
  slug?: string; // Optional, sparse index
  categoryId: string;
  
  // Duration and Language
  duration: {
    value: number;
    isInWeeks: boolean;
  };
  language: "english" | "spanish";
  status: "active" | "inactive";
  
  // Program Type References
  availableProgramTypes?: string[]; // ObjectId references
  defaultProgramType: string; // ObjectId reference
  
  // Media and Platform
  isPublished: boolean;
  platform?: {
    isIspring?: boolean;
    contentTitle?: string;
    contentItemId?: string;
  };
  
  // Social Media
  socialMedia?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
    website?: string;
  };
  
  // Content Structure
  modules?: ModuleV2[];
  whoisthisFor?: TargetAudienceV2[];
  wherecaniWork?: WorkPlaceV2[];
  clinicalProceduresGrouped?: ClinicalProcedureGroupV2[];
  hasclinical?: boolean;  // Flag to indicate if program has clinical components
  clinicalsScheduling?: {
    schedulingWindowDays: number;
    reactivationFeeCents: number;
    rescheduleFeeFirstMissCents: number;
    rescheduleFeeSecondMissCents: number;
    rescheduleFeeThirdMissCents: number;
    timezone: string;
    formVariant?: "standard_v1";
  };
  certificationExamScheduling?: {
    onCampusUrl?: string;
    onlineUrl?: string;
  };
  
  // Premium Information
  premiumShareCount: number;
  flyerTheme?: 'black' | 'red' | 'blue' | 'green' | 'purple' | 'rose';
  marketingTags?: string[];
  premiumDescription?: string;
  premiumTagline?: string;
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount?: number;
  premiumViewCount?: number;
  premiumLikeCount?: number;
  
  // Metadata
  createdBy?: string;
  updatedBy?: string;
}

export interface ModuleV2 {
  label: string;
  order: number;
  isActive: boolean;
  items: ModuleItemV2[];
}

export interface ModuleItemV2 {
  id: string;
  title: string;
  subtitle: string;
  isExam: boolean;
}

export interface CourseDataItemV2 {
  id: number;
  name: string;
  description?: string;
}

export interface TargetAudienceV2 {
  id: number;
  category: string;
  title: string;
  description: string;
}

export interface WorkPlaceV2 {
  id: number;
  title: string;
  description: string;
}

export interface ClinicalProcedureV2 {
  id: string;
  title: string;
  description?: string;
}

export interface ClinicalProcedureGroupV2 {
  label: string;
  subHeading?: string;
  items: ClinicalProcedureV2[];
}

export interface NationalCertificationV2 {
  isallIncluded: boolean;
  items: CertificationItemV2[];
}

export interface CertificationItemV2 {
  id: string;
  name: string;
  description: string;
}

export interface PodcastDataV2 {
  id: string;
  title: string;
  category: string;
  duration: string;
  imageUrl: string;
  audioUrl: string;
}

export interface ProgramComparisonV2 {
  showComparisonTable: boolean;
  comparisonCategories: ComparisonCategoryV2[];
}

export interface ComparisonCategoryV2 {
  id: string;
  name: string;
  description: string;
}

// ============================================================================
// API REQUEST/RESPONSE INTERFACES
// ============================================================================

export interface ProgramTypeV2Request {
  programId?: string; // ObjectId reference to program
  type: DeliveryModeV2;
  delivery: DeliveryModeDisplayV2;
  subtitle: string;
  about: string;
  videoUrl?: string;
  imageUrls?: string[];
  nationalCertifications?: any;
  podcastData?: PodcastDataV2[];
  schedule?: ProgramScheduleV2;
  keyFeatures?: KeyFeatureV2[];
  paymentOptions?: PaymentOptionV2[];
  workPlaces?: string[];
  faqs?: FAQV2[];
}

export interface CreateProgramV2Request {
  program: ProgramV2;
  programTypes: ProgramTypeV2Request[];
}

export interface CreateProgramV2Response {
  success: boolean;
  message: string;
  programId?: string;
  customProgramId?: string;
  programTypes?: ProgramTypeV2Request[];
  errors?: string[];
}

// ============================================================================
// FORM DATA INTERFACE (for frontend state management)
// ============================================================================

export interface ProgramFormV2 {
  // Step 1 - Common fields
  title: string;
  /** Optional: student-facing URL identifier. Backend normalizes + enforces uniqueness. */
  slug?: string;
  categoryId: string;
  duration: { value: number; isInWeeks: boolean };
  language: "english" | "spanish";
  status: "active" | "inactive";
  isPublished: boolean;
  platform?: { isIspring?: boolean; contentTitle?: string; contentItemId?: string };
  socialMedia: { 
    facebook?: string; 
    twitter?: string; 
    instagram?: string; 
    linkedin?: string; 
    youtube?: string; 
    website?: string; 
  };
  whoisthisFor: Array<{ id: number; category: string; title: string; description: string }>;
  wherecaniWork: Array<{ id: number; title: string; description: string }>;
  clinicalProceduresGrouped: Array<{ 
    label: string; 
    subHeading?: string; 
    items: Array<{ id: string; title: string }> 
  }>;
  hasclinical?: boolean;  // Flag to indicate if program has clinical components
  clinicalsScheduling?: {
    schedulingWindowDays: number;
    reactivationFeeCents: number;
    rescheduleFeeFirstMissCents: number;
    rescheduleFeeSecondMissCents: number;
    rescheduleFeeThirdMissCents: number;
    timezone: string;
    formVariant?: "standard_v1";
  };
  certificationExamScheduling?: {
    onCampusUrl?: string;
    onlineUrl?: string;
  };
  modules: Array<{ 
    label: string; 
    order: number; 
    isActive: boolean; 
    items: Array<{ id: string; title: string; subtitle: string; isExam: boolean }> 
  }>;
  // Note: thisCourseIncludesData and programComparison moved to ProgramTypeDataV2 (type-specific)
  premiumShareCount: number;
  flyerTheme: 'black' | 'red' | 'blue' | 'green' | 'purple' | 'rose';
  marketingTags: string[];
  premiumDescription?: string;
  premiumTagline?: string;
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount: number;
  premiumViewCount: number;
  premiumLikeCount: number;
  
  // Step 2 - Program type specific
  activeProgramType: DeliveryModeV2;
  programTypes: {
    hybrid?: ProgramTypeDataV2;
    'via-zoom'?: ProgramTypeDataV2;
    'on-campus'?: ProgramTypeDataV2;
    online?: ProgramTypeDataV2;
  };
  
  // Schedule data stored by program type
  programSchedule?: {
    hybrid?: any;
    'via-zoom'?: any;
    'on-campus'?: any;
    online?: any;
  };
}

// ============================================================================
// TRANSFORMATION FUNCTIONS
// ============================================================================

/**
 * Clean social media fields - convert empty strings to undefined
 */
const cleanSocialMedia = (socialMedia: any) => {
  if (!socialMedia) return socialMedia;
  
  const cleaned: any = {};
  Object.keys(socialMedia).forEach(key => {
    const value = socialMedia[key];
    cleaned[key] = (value && value.trim() !== '') ? value : undefined;
  });
  
  return cleaned;
};

/**
 * Get default schedule data for a program type
 */
// Backend V2 Simple Schedule Defaults
const getDefaultScheduleForType = (type: string): ProgramScheduleV2 => {
  switch (type) {
    case 'hybrid':
    case 'online':
      return {
        // Hybrid/Online: flat structure
        month: undefined,
        year: undefined,
        dates: []
      };
    case 'on-campus':
    case 'via-zoom':
      return {
        // On-campus/Via-zoom: flat batches array
        batches: []
      };
    default:
      return {};
  }
};

/**
 * Transform frontend form data to API format
 */
export const transformToAPIFormatV2 = (formData: ProgramFormV2): CreateProgramV2Request => {
  // Create the main program object
  const program: ProgramV2 = {
    title: formData.title,
    ...(formData.slug?.trim() ? { slug: formData.slug.trim() } : {}),
    categoryId: formData.categoryId,
    duration: formData.duration,
    language: formData.language,
    status: formData.status,
    isPublished: formData.isPublished,
    platform: formData.platform,
    socialMedia: cleanSocialMedia(formData.socialMedia),
    modules: formData.modules?.map(module => ({
      label: module.label,
      order: module.order,
      isActive: module.isActive,
      items: module.items.map(item => ({
        id: item.id,
        title: item.title,
        subtitle: item.subtitle,
        isExam: item.isExam
      }))
    })),
    whoisthisFor: formData.whoisthisFor?.map(item => ({
      id: item.id,
      category: item.category,
      title: item.title,
      description: item.description
    })),
    wherecaniWork: formData.wherecaniWork?.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description
    })),
    clinicalProceduresGrouped: formData.clinicalProceduresGrouped?.map(group => ({
      label: group.label,
      subHeading: group.subHeading,
      items: group.items.map(item => ({
        id: item.id,
        title: item.title
      }))
    })),
    hasclinical: formData.hasclinical,
    clinicalsScheduling: formData.hasclinical ? formData.clinicalsScheduling : undefined,
    certificationExamScheduling: formData.certificationExamScheduling,
    // Note: thisCourseIncludesData and programComparison now in programTypes (type-specific)
    premiumShareCount: formData.premiumShareCount,
    flyerTheme: formData.flyerTheme ?? 'black',
    marketingTags: formData.marketingTags ?? [],
    premiumDescription: formData.premiumDescription,
    premiumTagline: formData.premiumTagline,
    premiumRating: formData.premiumRating,
    premiumReviewCount: formData.premiumReviewCount,
    premiumStudentCount: formData.premiumStudentCount,
    premiumViewCount: formData.premiumViewCount,
    premiumLikeCount: formData.premiumLikeCount,
    // Set defaultProgramType to the active program type
    defaultProgramType: formData.activeProgramType || ""
  };

  // Helper function to clean and ensure correct schedule format for v2 backend
  const cleanScheduleData = (schedule: any): ProgramScheduleV2 => {
    if (!schedule) return {};
    
    const cleaned: ProgramScheduleV2 = {};
    
    // For hybrid/online:
    // Admin requirement: two modes only.
    // - Perpetual: recurringDaysOfMonth
    // - Specific: dates (YYYY-MM-DD)
    //
    // Keep legacy scheduleBatches only if it's the ONLY thing present.
    if (schedule.month) cleaned.month = schedule.month;
    if (schedule.year) cleaned.year = schedule.year;

    const hasDailyEnrollment = schedule.isDailyEnrollment === true;
    const hasDates = Array.isArray(schedule.dates) && schedule.dates.length > 0;
    const hasRecurring = Array.isArray(schedule.recurringDaysOfMonth) && schedule.recurringDaysOfMonth.length > 0;
    const hasLegacyBatches = Array.isArray(schedule.scheduleBatches) && schedule.scheduleBatches.length > 0;
    // PUT endpoint: clear by sending [] (null is invalid per backend schema).
    const legacyExplicitlyRemoved = Array.isArray(schedule.scheduleBatches) && schedule.scheduleBatches.length === 0;

    if (hasDailyEnrollment) {
      cleaned.isDailyEnrollment = true;
      // prevent ambiguous payloads
      cleaned.dates = undefined;
      cleaned.recurringDaysOfMonth = undefined;
      cleaned.scheduleBatches = legacyExplicitlyRemoved ? [] : undefined;
    } else if (hasDates) {
      cleaned.dates = schedule.dates;
      // prevent ambiguous payloads
      cleaned.recurringDaysOfMonth = undefined;
      cleaned.isDailyEnrollment = undefined;
      // If admin removed legacy, clear it explicitly with [].
      cleaned.scheduleBatches = legacyExplicitlyRemoved ? [] : undefined;
    } else if (hasRecurring) {
      cleaned.recurringDaysOfMonth = schedule.recurringDaysOfMonth;
      // prevent ambiguous payloads
      cleaned.dates = undefined;
      cleaned.isDailyEnrollment = undefined;
      cleaned.scheduleBatches = legacyExplicitlyRemoved ? [] : undefined;
    } else if (hasLegacyBatches) {
      // legacy-only fallback (read-only in UI)
      cleaned.scheduleBatches = schedule.scheduleBatches;
    } else {
      // keep empty values as-is (backend may accept empty schedule)
      if (Array.isArray(schedule.dates)) cleaned.dates = schedule.dates;
      if (Array.isArray(schedule.recurringDaysOfMonth)) cleaned.recurringDaysOfMonth = schedule.recurringDaysOfMonth;
      if (legacyExplicitlyRemoved) {
        cleaned.scheduleBatches = [];
      }
    }
    
    // For on-campus/via-zoom: extract flat batches array only
    if (schedule.batches && Array.isArray(schedule.batches)) {
      cleaned.batches = schedule.batches.map((batch: any) => ({
        batch: batch.batch,
        dates: batch.dates,
        recurringDaysOfMonth: batch.recurringDaysOfMonth,
        scheduleBatches: batch.scheduleBatches,
        daysOfWeek: batch.daysOfWeek,
        time: batch.time
      })).filter((b: any) => b.batch || b.dates || b.recurringDaysOfMonth || (b.scheduleBatches && b.scheduleBatches.length > 0) || b.daysOfWeek || b.time);
    }
    
    return cleaned;
  };

  // Create program types array
  const programTypes: ProgramTypeV2Request[] = Object.entries(formData.programTypes).map(([type, data]) => {
    // Get schedule data directly from data.schedule
    let schedule = data?.schedule;
    
    // If no schedule, create a default one based on program type
    if (!schedule) {
      schedule = getDefaultScheduleForType(type);
    }
    
    // Clean UI helper fields from schedule before sending to API
    schedule = cleanScheduleData(schedule);

    return {
      type: type as DeliveryModeV2,
      delivery: getDeliveryModeDisplay(type as DeliveryModeV2),
      subtitle: data?.subtitle || "",
      about: data?.about || "",
      videoUrl: data?.videoUrl,
      imageUrls: data?.imageUrls ?? [],
      nationalCertifications: data?.nationalCertifications,
      podcastData: data?.podcastData?.map(podcast => ({
        id: podcast.id,
        title: podcast.title,
        category: podcast.category,
        duration: podcast.duration,
        imageUrl: podcast.imageUrl,
        audioUrl: podcast.audioUrl
      })),
      thisCourseIncludesData: data?.thisCourseIncludesData?.map(item => ({
        id: item.id,
        name: item.name
      })),
      programComparison: data?.programComparison,
      schedule: schedule,
      keyFeatures: data?.keyFeatures,
      paymentOptions: data?.paymentOptions,
      workPlaces: data?.workPlaces,
      faqs: data?.faqs
    };
  });

  return {
    program,
    programTypes
  };
};

/**
 * Get display name for delivery mode
 */
export const getDeliveryModeDisplay = (mode: DeliveryModeV2): DeliveryModeDisplayV2 => {
  const mapping: Record<DeliveryModeV2, DeliveryModeDisplayV2> = {
    hybrid: "Hybrid",
    "via-zoom": "Via-Zoom",
    "on-campus": "On-Campus",
    online: "Online"
  };
  return mapping[mode];
};

/**
 * Get delivery mode from display name
 */
export const getDeliveryModeFromDisplay = (display: DeliveryModeDisplayV2): DeliveryModeV2 => {
  const mapping: Record<DeliveryModeDisplayV2, DeliveryModeV2> = {
    "Hybrid": "hybrid",
    "Via-Zoom": "via-zoom",
    "On-Campus": "on-campus",
    "Online": "online"
  };
  return mapping[display];
};

/**
 * Validate program form data
 */
export const validateProgramFormV2 = (formData: ProgramFormV2): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Required fields validation
  if (!formData.title?.trim()) {
    errors.push("Program title is required");
  }
  if (!formData.categoryId?.trim()) {
    errors.push("Category selection is required");
  }

  // Program types validation
  const programTypeKeys = Object.keys(formData.programTypes);
  if (programTypeKeys.length === 0) {
    errors.push("At least one program type must be configured");
  }

  // Validate each program type
  programTypeKeys.forEach(type => {
    const typeData = formData.programTypes[type as DeliveryModeV2];
    if (!typeData) return;

    if (!typeData.subtitle?.trim()) {
      errors.push(`${getDeliveryModeDisplay(type as DeliveryModeV2)}: Subtitle is required`);
    }

    if (!typeData.about?.trim()) {
      errors.push(`${getDeliveryModeDisplay(type as DeliveryModeV2)}: Description is required`);
    }

    if (!typeData.paymentOptions || typeData.paymentOptions.length === 0) {
      errors.push(`${getDeliveryModeDisplay(type as DeliveryModeV2)}: Payment options are required`);
    }

    if (!typeData.schedule) {
      errors.push(`${getDeliveryModeDisplay(type as DeliveryModeV2)}: Schedule configuration is required`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Create default program type data
 */
export const createDefaultProgramTypeData = (): ProgramTypeDataV2 => {
  return {
    subtitle: "",
    about: "",
    videoUrl: "",
    imageUrls: [],
    nationalCertifications: undefined,
    podcastData: [],
    thisCourseIncludesData: [],
    programComparison: undefined,
    paymentOptions: [],
    schedule: {
      // Simple v2 backend schema - no default fields
    },
    keyFeatures: [],
    faqs: [],
    workPlaces: []
  };
};

/**
 * Generate unique IDs for form items
 */
export const generateId = (): string => {
  return Date.now().toString();
};

/**
 * Generate IDs for array items
 */
export const generateIdsForArray = <T extends { id?: string }>(items: T[]): T[] => {
  return items.map(item => ({
    ...item,
    id: item.id || generateId()
  }));
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Deep clone an object
 */
export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Check if two objects are equal (shallow comparison)
 */
export const shallowEqual = (obj1: any, obj2: any): boolean => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) {
    return false;
  }
  
  for (let key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }
  
  return true;
};

/**
 * Format currency for display
 */
export const formatCurrency = (amount: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency
  }).format(amount);
};

/**
 * Format duration for display
 */
export const formatDuration = (duration: { value: number; isInWeeks: boolean }): string => {
  const { value, isInWeeks } = duration;
  const unit = isInWeeks ? (value === 1 ? "week" : "weeks") : (value === 1 ? "month" : "months");
  return `${value} ${unit}`;
};
